import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import * as moment from 'moment';

import 'rxjs/add/operator/toPromise';
import { tap } from 'rxjs/operators/tap';
import { catchError } from 'rxjs/operators/catchError';
import { Observable } from 'rxjs/Observable';

import { Stop } from './core/stop';
import { Coordinate } from './core/coordinate';
import { Route, BuiltRoute } from './core/route';
import { Schedule, StopSchedule, CommonSchedule, StopCommonSchedule } from './core/schedule';
import { Tram } from './core/tram';
import { Rail, RailPoint } from './core/rail';
import { PointOfInterest, POIType } from './core/poi';
import { Segment, SegmentInfo } from './core/segment';
import { Article, ArticleItem, ArticleType } from './core/article';

import { environment } from '../environments/environment';
import { TicketOffer } from './core/ticketOffer';
import { UserTicket, UserTicketPayment, UserTicketTrip, UserTicketTripPass } from './core/userTicket';
import { State, ResponseResult } from './core/state';
import { Review, ModerationStatus, EnhancedReview, CommentPack, IReviewComment, AdminComment, AbstractEnhancedReview } from './core/review';
import { StorageService } from './storage.service';
import { User } from './core/user';

@Injectable()
export class TramApiService {
    private jsonContentHeader: HttpHeaders = new HttpHeaders({ 'Content-Type': 'application/json' })
    private rootUrl = environment.apiUrl;
    private transportUrl = this.rootUrl + '/transport';
    private usersUrl = this.rootUrl + '/users';
    private userTicketsUrl = this.usersUrl + '/tickets';
    private companyUrl = this.rootUrl + '/company';

    private errors = {
        common: {
            internal: 'Внутренняя ошибка сервера',
            invalidData: 'Некорректные данные',
            required: {
                email: 'Необходимо ввести email'
            }
        },
        httpApi: {
            response: 'Проблема с ответом от сервера',
            connect: 'Проблема с соединением'
        },
        users: {
            invalidCredentials: 'Логин или пароль неверные',
            notExist: 'Пользователь не найден',
            resetPassword: 'Не удалось сбросить пароль. Доступ запрещен',
            changePassword: 'Не удалось сбросить пароль. Доступ запрещен',
            alreadyExist: 'Пользователь с такой почтой уже существует',
            tickets: {
                statusNotActive: 'Билет устарел',
                tripNotFound: 'Поездка не найдена',
                invalidTrip: 'Недействительная поездка',
                ticketOfferNotFound: 'Билет не найден',
                invalidTripsCount: 'Некорректное количество поездок',
                removeActive: 'Не удалось удалить активный билет',
                alreadyPaid: 'Уже оплачено',
                inactive: 'Неактивный билет',
                trip: {
                    invalidDate: 'Некорректная дата'
                }
            },
            feedbacks: {
                commentNotFound: 'Комментарий не найден'
            },
            accessDenied: 'Доступ запрещен'
        },
        api: {
            accessDenied: 'Доступ запрещен'
        },
        integration: {
            paymentService: 'Платежный сервис недоступен',
            ticketService: 'Сервис билетов недоступен',
            transportSystem: 'Транспортная система недоступна'
        }
    };

    constructor(
        private http: HttpClient,
        private storage: StorageService) { }

    //#region Users
    register(email: string, password: string): Promise<any> {
        const body = {
            email: email,
            password: password,
        };
        const headers = this.jsonContentHeader;

        return this.http.post(this.usersUrl, body, { headers })
            .toPromise().then(response => {
                this.storage.setToken(response['data']['token']);

                return response;
            })
            .catch(error => this.handleError(error));
    }
    setName(user: User, name: string): Promise<User> {
        const body = {
            name: name
        };

        const headers = this.jsonContentHeader;
        return this.http.put(`${this.usersUrl}/${user.id}`, body, { headers })
            .toPromise().then(response => {
                let user = <User>response['data'];
                return user;
            })
            .catch(error => this.handleError(error));
    }
    authenticate(email: string, password: string): Promise<any> {
        const body = {
            email: email,
            password: password
        };
        const headers = this.jsonContentHeader;

        return this.http.post(this.usersUrl + '/authenticate', body, { headers })
            .toPromise().then(response => {
                this.storage.setToken(response['data']['token']);

                return response;
            })
            .catch(error => this.handleError(error));
    }
    getUser(id: string): Promise<User> {
        return this.http.get(this.usersUrl + `/${id}`)
            .toPromise()
            .then(result => { return result['data'] as User; })
            .catch(error => this.handleError(error));
    }
    getCurrentUser(): Promise<User> {
        return this.http.get(`${this.usersUrl}/me`)
            .toPromise()
            .then(result => result['data'] as User)
            .catch(error => {
                if (error.status === 403) {
                    this.storage.clear();
                    location.reload();
                }

                return this.handleError(error);
            });
    }

    getUserTickets(id: string, limit: number, offset: number): Promise<ResponseResult<UserTicket[]>> {
        limit = limit > 0 ? limit : 50;
        offset = offset > 0 ? offset : 0;

        return this.http.get(this.usersUrl + `/${id}/tickets?limit=${limit}&offset=${offset}`)
            .toPromise()
            .then(result => {
                let response = new ResponseResult<UserTicket[]>();
                response.data = result['data'].map(r => this.wrapUserTicket(r));
                response.state = result['options'] as State;
                return response;
            })
            .catch(error => this.handleError(error));
    }
    initResetPassword(email: string): Promise<void> {
        const body = {
            email: email
        };
        const headers = this.jsonContentHeader;

        return this.http.post(this.usersUrl + '/password/email', body, { headers })
            .toPromise()
            .catch(error => this.handleError(error));
    }
    resetPassword(password: string, token: string): Promise<void> {
        const body = {
            password: password,
            token: token
        };

        const headers = this.jsonContentHeader;

        return this.http
            .post(this.usersUrl + '/password/reset', body, { headers })
            .toPromise()
            .catch(error => this.handleError(error));
    }
    changePassword(user: User, oldPassword: string, password: string): Promise<void> {
        const body = {
            oldPassword: oldPassword,
            password: password
        };

        let headers = this.jsonContentHeader;

        if (user) {
            return this.http
                .post(this.usersUrl + `/${user.id}/password/change`, body, { headers })
                .toPromise()
                .catch(error => this.handleError(error));
        }

        return Promise.reject(403);
    }
    //#endregion

    //#region POI

    getMalls() {
        return this.getPOIs('malls')
            .then(elements => this.prepareElements(elements));
    }
    getSupermarkets() {
        return this.getPOIs('supermarkets')
            .then(elements => this.prepareElements(elements));
    }

    getUniversities() {
        return this.getPOIs('universities')
            .then(elements => this.prepareElements(elements));
    }
    getKindergartens() {
        return this.getPOIs('kindergartens')
            .then(elements => this.prepareElements(elements));
    }
    getSchools() {
        return this.getPOIs('schools')
            .then(elements => this.prepareElements(elements));
    }
    getHospitals() {
        return this.getPOIs('hospitals')
            .then(elements => this.prepareElements(elements));
    }
    getPolice() {
        return this.getPOIs('police')
            .then(elements => this.prepareElements(elements));
    }
    getStations() {
        return this.getPOIs('stations')
            .then(elements => this.prepareElements(elements));
    }
    getEntertainment() {
        return this.getPOIs('entertainment')
            .then(elements => this.prepareElements(elements));
    }

    //#endregion

    //#region Stops
    /**
     * getting all stops;
     * use limit=0 and offset=0 to set default limit(50) and offset(0) 
     */
    getStops(limit: number, offset: number): Promise<Stop[]> {
        limit = limit > 0 ? limit : 50;
        offset = offset > 0 ? offset : 0;

        return this.http
            .get(`${this.transportUrl}/stops?limit=${limit}&offset=${offset}`)
            .toPromise()
            .then(result => result['data'] as Stop[])
            .catch(error => this.handleError(error));
    }

    getStop(id: number): Promise<Stop> {
        return this.http
            .get(`${this.transportUrl}/stop/${id}`)
            .toPromise()
            .then(result => result['data'] as Stop)
            .catch(error => this.handleError(error));
    }

    /**
     * getting stops of route by id;
     * isReverse is flag to point on getting stops forward of back route direction
     * use limit=0 and offset=0 to set default limit(50) and offset(0) 
     */
    getRouteStops(routeId: number, isReverse: boolean, limit: number, offset: number): Promise<Stop[]> {
        limit = limit > 0 ? limit : 50;
        offset = offset > 0 ? offset : 0;

        let url = isReverse
            ? `${this.transportUrl}/routes/${routeId}/stops?reverse=true&limit=${limit}&offset=${offset}`
            : `${this.transportUrl}/routes/${routeId}/stops?limit=${limit}&offset=${offset}`;

        return this.http
            .get(url)
            .toPromise()
            .then(result => result['data'] as Stop[])
            .catch(error => this.handleError(error));
    }
    //#endregion

    //#region Rails
    getRails(limit: number, offset: number): Promise<Rail[]> {
        limit = limit > 0 ? limit : 50;
        offset = offset > 0 ? offset : 0;

        return this.http
            .get(`${this.transportUrl}/rails?limit=${limit}&offset=${offset}`)
            .toPromise()
            .then(result => result['data'].map(this.wrapRail))
            .catch(error => this.handleError(error));
    }
    getRail(id: number): Promise<Rail> {
        return this.http
            .get(`${this.transportUrl}/rail/${id}`)
            .toPromise()
            .then(result => this.wrapRail(result['data']))
            .catch(error => this.handleError(error));
    }
    /**
     * getting stops of route by id;
     * use limit=0 and offset=0 to set default limit(50) and offset(0) 
     */
    getRouteRails(routeId: number, limit: number, offset: number): Promise<Stop[]> {
        limit = limit > 0 ? limit : 50;
        offset = offset > 0 ? offset : 0;

        return this.http
            .get(`${this.transportUrl}/routes/${routeId}/rails?limit=${limit}&offset=${offset}`)
            .toPromise()
            .then(result => result['data'].map(this.wrapRail))
            .catch(error => this.handleError(error));
    }
    //#endregion

    //#region Routes
    getRoutes(limit: number, offset: number): Promise<Route[]> {
        limit = limit > 0 ? limit : 50;
        offset = offset > 0 ? offset : 0;

        return this.http
            .get(`${this.transportUrl}/routes?limit=${limit}&offset=${offset}`)
            .toPromise()
            .then(result => result['data'].map(this.wrapRoute))
            .catch(error => this.handleError(error));
    }
    getRoute(id: number): Promise<Route> {
        return this.http
            .get(`${this.transportUrl}/routes/${id}`)
            .toPromise()
            .then(result => this.wrapRoute(result['data']))
            .catch(error => this.handleError(error));
    }
    getStopRoutes(stopId: number, limit: number, offset: number): Promise<Route[]> {
        limit = limit > 0 ? limit : 50;
        offset = offset > 0 ? offset : 0;

        return this.http
            .get(`${this.transportUrl}/stops/${stopId}/routes?limit=${limit}&offset=${offset}`)
            .toPromise()
            .then(result => result['data'].map(this.wrapRoute))
            .catch(error => this.handleError(error));
    }
    getRailRoutes(railId: number, limit: number, offset: number): Promise<Route[]> {
        limit = limit > 0 ? limit : 50;
        offset = offset > 0 ? offset : 0;

        return this.http
            .get(`${this.transportUrl}/rails/${railId}/routes?limit=${limit}&offset=${offset}`)
            .toPromise()
            .then(result => result['data'].map(this.wrapRoute))
            .catch(error => this.handleError(error));
    }

    //#endregion

    //#region Schedules
    /**
     * getting all schedules;
     * use limit=0 and offset=0 to set default limit(50) and offset(0) 
     */
    getSchedules(limit: number, offset: number): Promise<Schedule[]> {
        limit = limit > 0 ? limit : 50;
        offset = offset > 0 ? offset : 0;

        return this.http
            .get(`${this.transportUrl}/schedules?limit=${limit}&offset=${offset}`)
            .toPromise()
            .then(result => this.wrapSchedules(result['data']))
            .catch(error => this.handleError(error));
    }
    getSchedule(id: number): Promise<Schedule> {
        return this.http
            .get(`${this.transportUrl}/schedules/${id}`)
            .toPromise()
            .then(result => this.wrapSchedule(result['data']))
            .catch(error => this.handleError(error));
    }
    /**
     * getting schedules of route;
     * use limit=0 and offset=0 to set default limit(50) and offset(0) 
     */
    getRouteSchedules(routeId: number, limit: number, offset: number): Promise<Schedule[]> {
        limit = limit > 0 ? limit : 50;
        offset = offset > 0 ? offset : 0;

        return this.http
            .get(`${this.transportUrl}/routes/${routeId}/schedules?limit=${limit}&offset=${offset}`)
            .toPromise()
            .then(result => this.wrapSchedules(result['data']))
            .catch(error => this.handleError(error));
    }
    getRouteCommonSchedules(routeId: number, limit: number, offset: number): Promise<CommonSchedule[]> {
        limit = limit > 0 ? limit : 50;
        offset = offset > 0 ? offset : 0;

        return this.http
            .get(`${this.transportUrl}/routes/${routeId}/scheduleInfo?limit=${limit}&offset=${offset}`)
            .toPromise()
            .then(result => result['data'].map(this.wrapCommonSchedule))
            .catch(error => this.handleError(error));
    }
    getStopSchedule(stopId: number, routeId: number, limit: number, offset: number): Promise<StopCommonSchedule> {
        limit = limit > 0 ? limit : 50;
        offset = offset > 0 ? offset : 0;

        return this.http
        .get(`${this.transportUrl}/stops/${stopId}/scheduleInfo?limit=${limit}&offset=${offset}`)
        .toPromise()
        .then(result => {
            let schedules = result['data'].map(this.wrapStopCommonSchedule);
            return schedules.find(schedule => schedule['routeId'] === routeId);            
        })
        .catch(error => this.handleError(error));
    }
    getStopCommonSchedule(stopId: number, limit: number, offset: number): Promise<StopCommonSchedule> {
        limit = limit > 0 ? limit : 50;
        offset = offset > 0 ? offset : 0;

        return this.http
            .get(`${this.transportUrl}/stops/${stopId}/scheduleInfo?limit=${limit}&offset=${offset}`)
            .toPromise()
            .then(result => {
                let schedules = result['data'].map(this.wrapStopCommonSchedule);
                if (schedules.length > 0)
                    return schedules[0];
                else
                    return null;
            })
            .catch(error => this.handleError(error));
    }
    //#endregion

    //#region Trips
    getBuiltRoute(startStopId: number, finishStopId, limit: number, offset: number): Promise<BuiltRoute[]> {
        limit = limit > 0 ? limit : 50;
        offset = offset > 0 ? offset : 0;

        let body = {
            start: startStopId,
            finish: finishStopId
        };

        const headers = this.jsonContentHeader;

        return this.http.post(`${this.transportUrl}/trips`, body, { headers })
            .toPromise()
            .then(result => result['data'].map(this.wrapBuiltRoute))
            .catch(error => this.handleError(error));
    }
    //#endregion

    //#region Tickets

    createTicket(tripsCount: number, ticketOfferId: string, email: string): Promise<UserTicket> {
        const body = { tripsCount, ticketOfferId, email };
        const headers = this.jsonContentHeader;

        if (this.storage.isSensitiveDataExist.value) {
            return this.getCurrentUser()
                .then(u => {
                    if (u) {
                        body['userId'] = u.id;
                    }

                    return this.http
                        .post(`${this.userTicketsUrl}`, body, { headers })
                        .toPromise()
                        .then(result => this.wrapUserTicket(result['data']))
                        .catch(error => this.handleError(error));
                });
        } else {
            return this.http
                .post(`${this.userTicketsUrl}`, body, { headers })
                .toPromise()
                .then(result => this.wrapUserTicket(result['data']))
                .catch(error => this.handleError(error));
        }
    }

    getTicket(ticketId: string): Promise<UserTicket> {
        return this.http
            .get(`${this.userTicketsUrl}/${ticketId}`)
            .toPromise()
            .then(result => this.wrapUserTicket(result['data']))
            .catch(error => this.handleError(error));
    }

    payTicket(ticketId: string): Promise<string> {
        return this.http
            .post(`${this.userTicketsUrl}/${ticketId}/pay`, null)
            .toPromise()
            .then(result => result['data'])
            .catch(error => this.handleError(error));
    }

    sendTicketToEmail(ticketId: string, email: string): Promise<void> {
        let body = {
            email: email
        };

        let headers = this.jsonContentHeader;

        return this.http
            .post(`${this.userTicketsUrl}/${ticketId}/email`, body, { headers })
            .toPromise()
            .catch(error => this.handleError(error));
    }

    createTripWithTicket(ticketId: string, validFrom: Date): Promise<UserTicketTrip> {
        let body = {
            validFrom: validFrom
        };

        let headers = this.jsonContentHeader;

        return this.http
            .post(`${this.userTicketsUrl}/${ticketId}/trip`, body, { headers })
            .toPromise()
            .then(result => this.wrapUserTicketTrip(result['data']))
            .catch(error => this.handleError(error));
    }

    sendTripToEmail(ticketId: string, tripId: string, email: string): Promise<void> {
        let body = {
            email: email
        };

        let headers = this.jsonContentHeader;

        return this.http
            .post(`${this.userTicketsUrl}/${ticketId}/trip/${tripId}/email`, body, { headers })
            .toPromise()
            .catch(error => this.handleError(error));
    }

    getTripPassUrl(ticketId: string, tripId: string): string {
        return `${this.userTicketsUrl}/${ticketId}/trip/${tripId}/pass`;
    }

    //#endregion

    //#region Reviews
    getReviews(limit: number, offset: number): Promise<ResponseResult<Review[]>> {
        limit = limit > 0 ? limit : 50;
        offset = offset > 0 ? offset : 0;

        let headers = this.jsonContentHeader;

        return this.http.get(`${this.usersUrl}/feedbacks?limit=${limit}&offset=${offset}`, { headers })
            .toPromise()
            .then(result => {
                let response = new ResponseResult<Review[]>();
                response.data = result['data'].map(r => r as Review);
                response.state = result['options'] as State;
                return response;
            })
            .catch(error => this.handleError(error));
    }

    createReview(rate: number, message: string): Promise<Review> {
        const body = {
            rating: rate,
            message: message
        };

        let headers = this.jsonContentHeader;

        return this.http
            .post(`${this.usersUrl}/feedbacks`, body, { headers })
            .toPromise()
            .then(result => result['data'] as Review)
            .catch(error => this.handleError(error));
    }

    moderateComment(review: AbstractEnhancedReview, commentId: string, status: ModerationStatus, answer: string): Promise<IReviewComment> {
        const body: any = {
            status: status
        };

        if (answer) {
            body.comment = answer;
        }

        const headers = this.jsonContentHeader;
        const url = `${this.usersUrl}/feedbacks/${review.id}/comments/${commentId}/moderate`;

        return this.http.put(url, body, { headers })
            .toPromise()
            .then(result => {
                return result['data'] as IReviewComment;
            })
            .catch(error => this.handleError(error));
    }

    createComment(reviewId: string, comment: string): Promise<IReviewComment> {
        const body = {
            message: comment
        };
        const headers = this.jsonContentHeader;
        const url = `${this.usersUrl}/feedbacks/${reviewId}/comments`;

        return this.http.post(url, body, { headers })
            .toPromise()
            .then(result => {
                return result['data'] as IReviewComment;
            })
            .catch(error => this.handleError(error));
    }
    updateComment(reviewId: string, commentId: string, comment: string): Promise<IReviewComment> {
        const body = {
            message: comment
        };
        const headers = this.jsonContentHeader;
        const url = `${this.usersUrl}/feedbacks/${reviewId}/comments/${commentId}`;

        return this.http.put(url, body, { headers })
            .toPromise()
            .then(result => {
                return result['data'] as IReviewComment;
            })
            .catch(error => this.handleError(error));
    }
    deleteReview(reviewId: string): Promise<void> {
        const headers = this.jsonContentHeader;
        const url = `${this.usersUrl}/feedbacks/${reviewId}`;

        return this.http.delete(url, { headers })
            .toPromise()
            .then(response => {
                return response;
            })
            .catch(error => this.handleError(error));
    }
    deleteComment(reviewId: string, commentId: string): Promise<void> {
        const headers = this.jsonContentHeader;
        const url = `${this.usersUrl}/feedbacks/${reviewId}/comments/${commentId}`;

        return this.http.delete(url, { headers })
            .toPromise()
            .then(response => {
                return response;
            })
            .catch(error => this.handleError(error));
    }

    //#endregion

    private getPOIs(name: string) {
        const headers = this.jsonContentHeader;
        return this.http
            .get(`/assets/data/${name}.json`, { headers })
            .toPromise()
            .then(result => result['elements'])
            .catch(error => this.handleError(error));
    }

    private prepareElements(elements: any[]) {
        let points: PointOfInterest[] = [];

        let nodes = new Map<number, any>();
        let ways = new Map<number, any>();
        elements.forEach(e => {
            if (e.tags) {
                if (e.type === 'node') {
                    if (e.tags.shop) {
                        if (e.tags.shop === POIType.Mall) {
                            let point = this.wrapPointOfInterest(e, POIType.Mall);
                            points.push(point);
                        } else if (e.tags.shop === POIType.Supermarket) {
                            let point = this.wrapPointOfInterest(e, POIType.Supermarket);
                            points.push(point);
                        }
                    } else if (e.tags.amenity) {
                        let point: PointOfInterest;
                        switch (e.tags.amenity) {
                            case POIType.Kindergarten:
                            case POIType.School:
                            case POIType.University:
                            case POIType.Police:
                            case POIType.Hospital:
                            case POIType.Clinic:
                            case POIType.Doctor:

                            case POIType.Cafe:
                            case POIType.Cinema:
                            case POIType.Theatre:
                            case POIType.Pub:
                            case POIType.Nightclub:
                            case POIType.Restaurant:
                            case POIType.Bar:
                                let point = this.wrapPointOfInterest(e, e.tags.amenity as POIType);
                                points.push(point);
                                break;
                        }
                    } else if (e.tags.railway) {
                        if (e.tags.railway === POIType.RailwayStation
                            && e.tags.station === POIType.SubwayStation) {
                            let point = this.wrapPointOfInterest(e, POIType.SubwayStation);
                            points.push(point);
                        } else if (e.tags.railway === POIType.RailwayStation && !e.tags.station) {
                            let point = this.wrapPointOfInterest(e, POIType.RailwayStation);
                            points.push(point);
                        }
                    }

                } else if (e.type === 'way') {
                    ways.set(e.id, e);
                }
            } else {
                nodes.set(e.id, e);
            }
        });

        ways.forEach(w => {
            let leftLongitude: number = 0;
            let bottomLatitude: number = 0;
            let rightLongitude: number = 360;
            let topLatitude: number = 360;

            let centerLatitude: number = 0;
            let centerLongitude: number = 0;

            if (w.nodes && w.nodes instanceof Array) {

                w.nodes.forEach(id => {
                    if (!nodes.has(id))
                        return;
                    let node = nodes.get(id);
                    if (node.lon > leftLongitude)
                        leftLongitude = node.lon;
                    if (node.lat < topLatitude)
                        topLatitude = node.lat;
                    if (node.lon < rightLongitude)
                        rightLongitude = node.lon;
                    if (node.lat > bottomLatitude)
                        bottomLatitude = node.lat;
                });
            }

            centerLongitude = rightLongitude + ((leftLongitude - rightLongitude) / 2);
            centerLatitude = topLatitude + ((bottomLatitude - topLatitude) / 2);

            w.lat = centerLatitude;
            w.lon = centerLongitude;

            if (w.tags.shop) {
                let point = this.wrapPointOfInterest(w, w.tags.shop as POIType);
                points.push(point);
            } else if (w.tags.amenity) {
                let point = this.wrapPointOfInterest(w, w.tags.amenity as POIType);
                points.push(point);
            }
        });

        return points;
    }

    private wrapPointOfInterest(raw: any, type: POIType): PointOfInterest {
        let point = new PointOfInterest();
        point.id = raw.id;
        point.position = new Coordinate();
        point.position.latitude = raw.lat;
        point.position.longitude = raw.lon;
        point.type = type;
        if (raw.tags && raw.tags.name)
            point.name = raw.tags.name;
        else
            point.name = 'нет данных';

        return point;
    }

    private wrapStop(raw: any): Stop {
        let newStop = new Stop();
        newStop.id = raw.id;
        newStop.name = raw.name;
        newStop.coordinate = raw.coordinate as Coordinate;

        return newStop;
    }
    private wrapRail(raw: any): Rail {
        let rail = new Rail();
        rail.id = raw.id;
        rail.points = raw.points as RailPoint[];

        return rail;
    }

    private wrapRoute(raw: any): Route {
        let route = new Route();
        route.id = raw.id;
        route.number = raw.routeNumber;
        route.stopIds = raw.stopIds;
        route.reverseStopIds = raw.reverseStopIds;
        route.railIds = raw.railIds ? raw.railIds : [];
        route.reverseRailIds = raw.reverseRailIds ? raw.reverseRailIds : [];

        return route;
    }
    private wrapBuiltRoute(raw: any): BuiltRoute {
        let route = new BuiltRoute();
        route.id = raw.id;
        route.departureTime = raw.departureTime;
        route.arrivalTime = raw.arrivalTime;
        route.duration = raw.duration;
        route.segments = [];

        raw.segments.forEach(s => {
            let segment = new Segment();
            segment.routeId = s.routeId;
            segment.scheduleId = s.scheduleId;
            segment.startInfo = new SegmentInfo();
            segment.startInfo.stopId = s.segmentStartInfo.stopId;
            segment.startInfo.time = s.segmentStartInfo.time;

            segment.finishInfo = new SegmentInfo();
            segment.finishInfo.stopId = s.segmentStopInfo.stopId;
            segment.finishInfo.time = s.segmentStopInfo.time;

            route.segments.push(segment);
        });

        return route;
    }

    private wrapSchedules(raw: any): Schedule[] {
        let schedules: Schedule[] = [];

        if (raw && raw.length && raw.length > 0) {
            for (let r of raw) {
                let schedule = this.wrapSchedule(r);
                schedules.push(schedule);
            }
        }
        return schedules;
    }
    private wrapSchedule(raw: any): Schedule {
        let schedule = new Schedule();
        schedule.id = raw.id;
        schedule.routeId = raw.routeId;
        schedule.isReverseTrip = raw.isReverseTrip;
        schedule.dayType = raw.dayType;
        schedule.stopSchedules = raw.stopSchedules.map(s => {
            let stopSchedule = new StopSchedule();
            stopSchedule.stopId = s.stopId;
            stopSchedule.arrivalTime = s.arrivalTime;
            stopSchedule.departureTime = s.departureTime;

            return stopSchedule;
        });

        return schedule;
    }
    private wrapCommonSchedule(raw: any): CommonSchedule {
        let schedule = new CommonSchedule();
        schedule.routeId = raw.routeId;
        schedule.isReverseTrip = raw.isReverseTrip;
        schedule.dayType = raw.dayType;
        schedule.interval = raw.interval;
        schedule.startTime = raw.startTime;
        schedule.endTime = raw.endTime;

        return schedule;
    }
    private wrapStopCommonSchedule(raw: any): StopCommonSchedule {
        let schedule = new StopCommonSchedule();
        schedule.stopId = raw.stopId;
        schedule.routeId = raw.routeId;
        schedule.isReverseRoute = raw.isReverseRoute;
        schedule.dayType = raw.dayType;
        schedule.departureTimes = raw.departureTimes;

        return schedule;
    }

    private wrapUserTicket(raw: any): UserTicket {
        let userTicket = new UserTicket();

        userTicket.id = raw.id;
        userTicket.created = raw.created;
        userTicket.updated = raw.updated;
        userTicket.tripsCount = raw.tripsCount;
        userTicket.remainingTripsCount = raw.remainingTripsCount;
        userTicket.cost = raw.cost;
        userTicket.orderNumber = raw.orderNumber;
        userTicket.status = raw.status;
        userTicket.payment = this.wrapUserTicketPayment(raw.payment);
        userTicket.trips = this.wrapUserTicketTrips(raw.trips);

        return userTicket;
    }

    private wrapUserTicketPayment(raw: any): UserTicketPayment {
        let userTicketPayment = new UserTicketPayment();

        userTicketPayment.created = raw.created;
        userTicketPayment.updated = raw.updated;
        userTicketPayment.status = raw.status;
        userTicketPayment.amount = raw.amount;
        userTicketPayment.currency = raw.currency;
        userTicketPayment.actionCode = raw.actionCode;
        userTicketPayment.actionCodeDescription = raw.actionCodeDescription;

        return userTicketPayment;
    }

    private wrapUserTicketTrips(raw: any[]): UserTicketTrip[] {
        let userTicketTrips = [];

        if (raw && raw.length && raw.length > 0) {
            for (let r of raw) {
                userTicketTrips.push(this.wrapUserTicketTrip(r));
            }
        }

        return userTicketTrips;
    }

    private wrapUserTicketTrip(raw: any): UserTicketTrip {
        let userTicketTrip = new UserTicketTrip();

        userTicketTrip.id = raw.id;
        userTicketTrip.created = raw.created;
        userTicketTrip.updated = raw.updated;
        userTicketTrip.pass = raw.pass ? this.wrapUserTicketTripPass(raw.pass) : null;

        return userTicketTrip;
    }

    private wrapUserTicketTripPass(raw: any): UserTicketTripPass {
        let userTicketTripPass = new UserTicketTripPass();

        userTicketTripPass.created = raw.created;
        userTicketTripPass.updated = raw.updated;
        userTicketTripPass.cost = raw.cost;
        userTicketTripPass.validFrom = raw.validFrom;
        userTicketTripPass.validTo = raw.validTo;
        userTicketTripPass.qrCode = raw.qrCode;
        userTicketTripPass.isUsed = raw.isUsed;
        userTicketTripPass.isExpired = raw.isExpired;
        userTicketTripPass.usedStatusChangeDate = raw.usedStatusChangeDate;
        userTicketTripPass.usedStatusTramInfo = raw.usedStatusTramInfo;

        return userTicketTripPass;
    }

    private handleError(error: HttpErrorResponse): Promise<any> {
        const message = this.resolveError(error);
        if (message) {
            return Promise.reject(message);
        }

        return Promise.reject(error);
    }

    private resolveError(res: HttpErrorResponse): any {
        if (res.error && res.error.errors && res.error.errors.length > 0) {
            const parts = res.error.errors[0].split('.');
            if (parts.length <= 0) {
                return null;
            }

            let result = this;
            for (const part of parts) {
                result = result[part];
                if (!result) {
                    break;
                }
            }

            return result;
        }
    }
}



// WEBPACK FOOTER //
// D:/programming/Чижик_Api_Site_Sources-Electroline/front/src/app/tram-api.service.ts